import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { updateProfile, fetchProfile } from '../../store/slices/profileSlice';
import { updateNotificationSettings } from '../../store/slices/notificationsSlice';
import PageHeader from '../../components/common/PageHeader';
import { FaUser, FaBell, FaShieldAlt, FaKey, FaSave, FaSpinner, FaMapMarkerAlt, FaExclamationTriangle } from 'react-icons/fa';
import { toast } from 'react-toastify';
import './Settings.css';

const Settings = () => {
  const dispatch = useDispatch();
  const { currentProfile, loading: profileLoading } = useSelector((state) => state.profile);
  const { settings: notificationSettings, loading: notificationsLoading } = useSelector((state) => state.notifications);
  const { user } = useSelector((state) => state.auth);

  const [activeTab, setActiveTab] = useState('profile');
  const [profileForm, setProfileForm] = useState({
    first_name: '',
    last_name: '',
    bio: '',
    location: '',
    occupation: '',
    education: '',
    interests: '',
    gender: '',
    relationship_status: '',
    date_of_birth: ''
  });
  const [errors, setErrors] = useState({});
  const [notificationForm, setNotificationForm] = useState({
    likes: true,
    comments: true,
    follows: true,
    matches: true,
    email: true,
    push: true
  });
  const [saving, setSaving] = useState(false);
  const [locationLoading, setLocationLoading] = useState(false);
  const [locationError, setLocationError] = useState('');

  // Charger le profil au montage du composant
  useEffect(() => {
    if (user?.id) {
      dispatch(fetchProfile(user.id));
    }
  }, [dispatch, user?.id]);

  useEffect(() => {
    if (currentProfile) {
      setProfileForm({
        first_name: currentProfile.first_name || '',
        last_name: currentProfile.last_name || '',
        bio: currentProfile.bio || '',
        location: currentProfile.location || '',
        occupation: currentProfile.occupation || '',
        education: currentProfile.education || '',
        interests: currentProfile.interests || '',
        gender: currentProfile.gender || '',
        relationship_status: currentProfile.relationship_status || '',
        date_of_birth: currentProfile.date_of_birth ? currentProfile.date_of_birth.split('T')[0] : ''
      });
    }
  }, [currentProfile]);

  useEffect(() => {
    if (notificationSettings) {
      setNotificationForm(notificationSettings);
    }
  }, [notificationSettings]);

  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    setProfileForm(prev => ({ ...prev, [name]: value }));
  };

  const handleNotificationChange = async (setting, value) => {
    setNotificationForm(prev => ({ ...prev, [setting]: value }));
    await dispatch(updateNotificationSettings({ [setting]: value }));
  };

  // Fonction pour détecter automatiquement la localisation
  const detectLocation = async () => {
    setLocationLoading(true);
    setLocationError('');

    if (!navigator.geolocation) {
      setLocationError('La géolocalisation n\'est pas supportée par votre navigateur');
      setLocationLoading(false);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;

        try {
          // Reverse geocoding avec Nominatim (OpenStreetMap)
          const response = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&addressdetails=1`
          );

          if (!response.ok) {
            throw new Error('Erreur lors de la récupération de l\'adresse');
          }

          const data = await response.json();

          // Extraire l'adresse formatée
          let address = '';
          if (data && data.address) {
            const addr = data.address;
            const city = addr.city || addr.town || addr.village || addr.municipality;
            const country = addr.country;

            if (city && country) {
              address = `${city}, ${country}`;
            } else if (city) {
              address = city;
            } else if (country) {
              address = country;
            } else {
              address = data.display_name.split(',')[0]; // Fallback
            }
          }

          if (address) {
            setProfileForm(prev => ({ ...prev, location: address }));
            setLocationError('');
          } else {
            setLocationError('Impossible de déterminer votre adresse exacte');
          }
        } catch (error) {
          console.error('Erreur de géocodage:', error);
          setLocationError('Erreur lors de la récupération de votre adresse');
        }

        setLocationLoading(false);
      },
      (error) => {
        console.error('Erreur de géolocalisation:', error);

        let errorMessage = '';
        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage = 'Vous avez refusé l\'accès à votre localisation. Veuillez autoriser l\'accès dans les paramètres de votre navigateur.';
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage = 'Votre position n\'a pas pu être déterminée.';
            break;
          case error.TIMEOUT:
            errorMessage = 'La demande de localisation a expiré.';
            break;
          default:
            errorMessage = 'Une erreur inconnue s\'est produite.';
            break;
        }

        setLocationError(errorMessage);
        setLocationLoading(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 300000 // 5 minutes
      }
    );
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setErrors({});
    try {
      const result = await dispatch(updateProfile(profileForm));
      if (result.meta.requestStatus === 'fulfilled') {
        toast.success('Profil mis à jour avec succès !');
      } else {
        setErrors(result.payload?.data?.errors || { general: 'Erreur lors de la mise à jour du profil' });
      }
    } catch (error) {
      setErrors({ general: 'Une erreur inattendue s\'est produite' });
    } finally {
      setSaving(false);
    }
  };

  const tabs = [
    { id: 'profile', label: 'Profil', icon: FaUser },
    { id: 'notifications', label: 'Notifications', icon: FaBell },
    { id: 'privacy', label: 'Confidentialité', icon: FaShieldAlt },
    { id: 'security', label: 'Sécurité', icon: FaKey }
  ];

  return (
    <div className="settings-page">
      <div className="settings-container">
        <PageHeader
          title="⚙️ Paramètres"
          subtitle="Gérez vos préférences et informations personnelles"
        />

        <div className="settings-content">
          <div className="settings-sidebar">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  className={`settings-tab ${activeTab === tab.id ? 'active' : ''}`}
                  onClick={() => setActiveTab(tab.id)}
                >
                  <Icon className="tab-icon" />
                  {tab.label}
                </button>
              );
            })}
          </div>

          <div className="settings-main">
            {activeTab === 'profile' && (
              <div className="settings-section">
                <h2>Informations du profil</h2>
                {errors.general && (
                  <div className="error-message">
                    <span>{errors.general}</span>
                  </div>
                )}
                <form onSubmit={handleProfileSubmit} className="settings-form">
                  <div className="form-row">
                    <div className="form-group">
                      <label>Prénom</label>
                      <input
                        type="text"
                        name="first_name"
                        value={profileForm.first_name}
                        onChange={handleProfileChange}
                        placeholder="Votre prénom"
                      />
                    </div>
                    <div className="form-group">
                      <label>Nom</label>
                      <input
                        type="text"
                        name="last_name"
                        value={profileForm.last_name}
                        onChange={handleProfileChange}
                        placeholder="Votre nom"
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Biographie</label>
                    <textarea
                      name="bio"
                      value={profileForm.bio}
                      onChange={handleProfileChange}
                      placeholder="Parlez de vous..."
                      rows={3}
                    />
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Localisation</label>
                      <div className="location-input-group">
                        <input
                          type="text"
                          name="location"
                          value={profileForm.location}
                          onChange={handleProfileChange}
                          placeholder="Ville, Pays"
                          className="location-input"
                        />
                        <button
                          type="button"
                          className="detect-location-btn"
                          onClick={detectLocation}
                          disabled={locationLoading}
                          title="Détecter automatiquement ma localisation"
                        >
                          {locationLoading ? (
                            <FaSpinner className="spinner" />
                          ) : (
                            <FaMapMarkerAlt />
                          )}
                        </button>
                      </div>
                      {locationError && (
                        <div className="location-error">
                          <FaExclamationTriangle className="error-icon" />
                          <span>{locationError}</span>
                        </div>
                      )}
                    </div>
                    <div className="form-group">
                      <label>Date de naissance</label>
                      <input
                        type="date"
                        name="date_of_birth"
                        value={profileForm.date_of_birth}
                        onChange={handleProfileChange}
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Profession</label>
                      <input
                        type="text"
                        name="occupation"
                        value={profileForm.occupation}
                        onChange={handleProfileChange}
                        placeholder="Votre profession"
                      />
                    </div>
                    <div className="form-group">
                      <label>Éducation</label>
                      <input
                        type="text"
                        name="education"
                        value={profileForm.education}
                        onChange={handleProfileChange}
                        placeholder="Votre formation"
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Genre</label>
                      <select name="gender" value={profileForm.gender} onChange={handleProfileChange}>
                        <option value="">Sélectionner</option>
                        <option value="male">Homme</option>
                        <option value="female">Femme</option>
                        <option value="other">Autre</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Statut relationnel</label>
                      <select name="relationship_status" value={profileForm.relationship_status} onChange={handleProfileChange}>
                        <option value="">Sélectionner</option>
                        <option value="single">Célibataire</option>
                        <option value="in_relationship">En couple</option>
                        <option value="married">Marié(e)</option>
                        <option value="divorced">Divorcé(e)</option>
                      </select>
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Centres d'intérêt</label>
                    <input
                      type="text"
                      name="interests"
                      value={profileForm.interests}
                      onChange={handleProfileChange}
                      placeholder="Séparez par des virgules"
                    />
                  </div>

                  <button type="submit" className="save-btn" disabled={saving}>
                    {saving ? <><FaSpinner className="spinner" /> Sauvegarde...</> : <><FaSave /> Sauvegarder</>}
                  </button>
                </form>
              </div>
            )}

            {activeTab === 'notifications' && (
              <div className="settings-section">
                <h2>Paramètres de notifications</h2>
                <div className="notification-settings">
                  <div className="setting-group">
                    <h3>Notifications push</h3>
                    <div className="setting-item">
                      <label>
                        <input
                          type="checkbox"
                          checked={notificationForm.likes}
                          onChange={(e) => handleNotificationChange('likes', e.target.checked)}
                        />
                        Likes sur mes LoveShots
                      </label>
                    </div>
                    <div className="setting-item">
                      <label>
                        <input
                          type="checkbox"
                          checked={notificationForm.comments}
                          onChange={(e) => handleNotificationChange('comments', e.target.checked)}
                        />
                        Commentaires sur mes LoveShots
                      </label>
                    </div>
                    <div className="setting-item">
                      <label>
                        <input
                          type="checkbox"
                          checked={notificationForm.follows}
                          onChange={(e) => handleNotificationChange('follows', e.target.checked)}
                        />
                        Nouveaux abonnés
                      </label>
                    </div>
                    <div className="setting-item">
                      <label>
                        <input
                          type="checkbox"
                          checked={notificationForm.matches}
                          onChange={(e) => handleNotificationChange('matches', e.target.checked)}
                        />
                        Nouveaux matches
                      </label>
                    </div>
                  </div>

                  <div className="setting-group">
                    <h3>Notifications par email</h3>
                    <div className="setting-item">
                      <label>
                        <input
                          type="checkbox"
                          checked={notificationForm.email}
                          onChange={(e) => handleNotificationChange('email', e.target.checked)}
                        />
                        Résumé hebdomadaire
                      </label>
                    </div>
                  </div>

                  <div className="setting-group">
                    <h3>Notifications push</h3>
                    <div className="setting-item">
                      <label>
                        <input
                          type="checkbox"
                          checked={notificationForm.push}
                          onChange={(e) => handleNotificationChange('push', e.target.checked)}
                        />
                        Notifications push
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'privacy' && (
              <div className="settings-section">
                <h2>Confidentialité</h2>
                <div className="privacy-settings">
                  <div className="setting-group">
                    <h3>Visibilité du profil</h3>
                    <div className="setting-item">
                      <label>
                        <input type="radio" name="profile_visibility" value="public" defaultChecked />
                        Profil public
                      </label>
                    </div>
                    <div className="setting-item">
                      <label>
                        <input type="radio" name="profile_visibility" value="private" />
                        Profil privé
                      </label>
                    </div>
                  </div>

                  <div className="setting-group">
                    <h3>Qui peut me contacter</h3>
                    <div className="setting-item">
                      <label>
                        <input type="radio" name="contact" value="everyone" defaultChecked />
                        Tout le monde
                      </label>
                    </div>
                    <div className="setting-item">
                      <label>
                        <input type="radio" name="contact" value="matches" />
                        Mes matches uniquement
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'security' && (
              <div className="settings-section">
                <h2>Sécurité</h2>
                <div className="security-settings">
                  <div className="setting-group">
                    <h3>Changer le mot de passe</h3>
                    <div className="form-group">
                      <label>Mot de passe actuel</label>
                      <input type="password" placeholder="Mot de passe actuel" />
                    </div>
                    <div className="form-group">
                      <label>Nouveau mot de passe</label>
                      <input type="password" placeholder="Nouveau mot de passe" />
                    </div>
                    <div className="form-group">
                      <label>Confirmer le mot de passe</label>
                      <input type="password" placeholder="Confirmer le mot de passe" />
                    </div>
                    <button className="save-btn">Changer le mot de passe</button>
                  </div>

                  <div className="setting-group">
                    <h3>Sessions actives</h3>
                    <p>Gérez vos sessions connectées</p>
                    <button className="secondary-btn">Voir les sessions</button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;
